import React, { CSSProperties, useRef } from "react";
import { useDrop, DropTargetMonitor, XYCoord, useDrag } from "react-dnd";
import { Group, ActionIcon, Text } from "@mantine/core";
import { IconMinus } from "@tabler/icons-react";
import { ReportElement } from "./types";

const ItemType = 'ITEM';

interface DraggableItemProps {
  field_code: string;
  text: string;
  index: number;
  element: ReportElement
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  removeElement: (element: ReportElement) => void
}

interface DragItem {
  index: number;
  field_code: string;
  type: string;
}

export const DraggableElement: React.FC<DraggableItemProps> = ({ field_code, text, index, moveItem, removeElement, element }) => {
    const ref = useRef<HTMLDivElement>(null);

    const [, drop] = useDrop({
      accept: ItemType,
      hover(item: DragItem, monitor: DropTargetMonitor) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) {
          return;
        }

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        moveItem(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: ItemType,
      item: { field_code, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const style: CSSProperties = {
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
      };

    drag(drop(ref));

    return (
      <Group ref={ref} style={style} justify='space-between'>
          <Group wrap='nowrap' w='40%'>
              <ActionIcon color='red' variant='transparent' onClick={() => removeElement(element)}>
                  <IconMinus />
              </ActionIcon>
              <Text style={style}>{text}</Text>
          </Group>
          <Text w='40%' style={style}>{text}</Text>
      </Group>
    );
  };